import { useState, useCallback, useEffect, useRef } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { authApi, mapAuthError } from "@/lib/api/auth";
import { forgotPasswordStore } from "@/lib/forgotPasswordStore";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

export function useForgotPasswordOTP() {
  const router = useRouter();
  const params = useLocalSearchParams<{ email?: string }>();
  const email = params.email ?? "";

  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Start cooldown on mount (OTP was just sent from the previous screen)
  useEffect(() => {
    startCooldown();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resendMutation = useMutation({
    mutationFn: () => authApi.forgotPassword({ email }),
    onSuccess: (data) => {
      forgotPasswordStore.setToken(data.otp_token);
      forgotPasswordStore.setCode("");
      setCode("");
      startCooldown();
    },
  });

  const handleCodeChange = useCallback(
    (value: string) => {
      setCode(value);
      if (localError) setLocalError(null);
    },
    [localError]
  );

  const handleVerify = useCallback(() => {
    if (code.length < OTP_LENGTH) return;
    if (!forgotPasswordStore.getToken()) {
      setLocalError("auth.otp.error.invalid");
      return;
    }
    // Store the OTP code; actual verification happens on the reset-password screen
    forgotPasswordStore.setCode(code);
    router.replace("/(auth)/forgot-password/reset-password" as never);
  }, [code, router]);

  const handleResend = useCallback(async () => {
    if (countdown > 0 || resendMutation.isPending) return;
    if (!email) return;
    await resendMutation.mutateAsync().catch(() => {});
  }, [countdown, resendMutation.isPending, resendMutation.mutateAsync, email]); // eslint-disable-line react-hooks/exhaustive-deps

  const rawResendError = resendMutation.isError ? resendMutation.error : null;

  const error: string | null = localError ?? (rawResendError ? mapAuthError(rawResendError) : null);

  const canResend = countdown === 0 && !resendMutation.isPending;
  const isComplete = code.length === OTP_LENGTH;

  return {
    code,
    email,
    isVerifying: false,
    isResending: resendMutation.isPending,
    error,
    countdown,
    canResend,
    isComplete,
    handleCodeChange,
    handleVerify,
    handleResend,
    router,
  };
}
